import {
  ArrowRight,
  BookOpenText,
  Check,
  CheckCircle,
  Circle,
  Lightbulb,
  X,
} from "@phosphor-icons/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { pathWithLocale, type Locale } from "../i18n";

type GuideExperience = "governance" | "workbench";

interface GuideStep {
  id: string;
  title: string;
  description: string;
  time: string;
  to: string;
}

interface ProductGuideProps {
  experience: GuideExperience;
  locale: Locale;
}

const GUIDE_COPY = {
  governance: {
    en: {
      eyebrow: "Guided sample-room tour",
      title: "Leader quick-start",
      intro: "Follow the knowledge supply chain from source connection to trusted AI use.",
      nudge: "New here? Start with the 5-step product tour",
      tip: "The sample room is already populated. You can safely explore without changing live source systems.",
      steps: [
        ["overview", "Read the supply overview", "See risk, work queues, and current knowledge health in one place.", "1 min", "/app"],
        ["connect", "Connect a knowledge source", "Review the read-only connector flow and run a synthetic sample sync.", "2 min", "/app/connections"],
        ["inspect", "Inspect governed knowledge", "Open a sample object and trace its source, trust evidence, and downstream use.", "2 min", "/app/knowledge"],
        ["govern", "Resolve duplicates and conflicts", "See how Assay recommends survivorship and routes material ambiguity to people.", "3 min", "/app/duplicates"],
        ["activate", "Ask a trusted question", "Search across approved knowledge and verify the answer through citations.", "2 min", "/app/search"],
      ],
    },
    zh: {
      eyebrow: "样板间引导",
      title: "负责人体验路线",
      intro: "沿着知识供应链，从来源接入一路体验到可信 AI 使用。",
      nudge: "第一次使用？从 5 步体验路线开始",
      tip: "样板间已经准备好完整数据，可以放心体验，不会改动任何真实来源系统。",
      steps: [
        ["overview", "先看知识供应概览", "一屏了解风险、待办队列和当前知识健康度。", "1 分钟", "/app"],
        ["connect", "连接一个知识源", "查看只读接入流程，并运行一次安全的合成样板同步。", "2 分钟", "/app/connections"],
        ["inspect", "检查一条治理后知识", "打开样板知识，追溯来源、可信证据与下游使用情况。", "2 分钟", "/app/knowledge"],
        ["govern", "处理重复与冲突", "查看 Assay 如何推荐权威版本，并把重大歧义交给人来裁决。", "3 分钟", "/app/duplicates"],
        ["activate", "完成一次可信问答", "在已批准知识中检索，并通过引用核验回答依据。", "2 分钟", "/app/search"],
      ],
    },
  },
  workbench: {
    en: {
      eyebrow: "Employee quick-start",
      title: "Employee quick-start",
      intro: "Finish real work with governed knowledge, without learning the governance console.",
      nudge: "New here? Try the 3-step workbench tour",
      tip: "Try the Policy Q&A workflow first. Every answer keeps permission-aware citations attached.",
      steps: [
        ["template", "Choose a ready-made workflow", "Start with Policy Q&A, evidence synthesis, or an executive brief.", "1 min", "/work#recommended-workflows"],
        ["ask", "Complete a trusted answer", "Ask a real business question and keep the trust threshold at 70 or above.", "2 min", "/work#composer"],
        ["verify", "Verify evidence and citations", "Open a cited source to confirm authority, access, and the exact supporting content.", "2 min", "/work#recent-work"],
      ],
    },
    zh: {
      eyebrow: "业务工作台引导",
      title: "业务同学体验路线",
      intro: "不需要学习治理后台，也能直接用可信知识完成真实工作。",
      nudge: "第一次使用？从 3 步工作台体验开始",
      tip: "建议先试用“制度问答”。每个回答都会保留权限校验后的引用依据。",
      steps: [
        ["template", "选择一个现成工作流", "从制度问答、证据综合或管理层简报开始。", "1 分钟", "/work#recommended-workflows"],
        ["ask", "完成一次可信问答", "提出真实业务问题，并将最低可信度保持在 70 以上。", "2 分钟", "/work#composer"],
        ["verify", "核验依据与引用", "打开引用来源，确认权威状态、访问权限和原始支撑内容。", "2 分钟", "/work#recent-work"],
      ],
    },
  },
} as const;

function readCompleted(key: string): string[] {
  try {
    const value: unknown = JSON.parse(window.localStorage.getItem(key) ?? "[]") as unknown;
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
}

function saveCompleted(key: string, value: string[]): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // The guide remains usable when storage is unavailable.
  }
}

export function ProductGuide({ experience, locale }: ProductGuideProps) {
  const copy = GUIDE_COPY[experience][locale];
  const progressKey = `assay.guide.v1.${experience}.progress`;
  const seenKey = `assay.guide.v1.${experience}.seen`;
  const steps = useMemo<GuideStep[]>(
    () => copy.steps.map(([id, title, description, time, to]) => ({ id, title, description, time, to })),
    [copy.steps],
  );
  const [open, setOpen] = useState(false);
  const [showNudge, setShowNudge] = useState(() => {
    try {
      return window.localStorage.getItem(seenKey) !== "true";
    } catch {
      return true;
    }
  });
  const [completed, setCompleted] = useState<string[]>(() => readCompleted(progressKey));
  const closeRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);

  const markSeen = () => {
    setShowNudge(false);
    try {
      window.localStorage.setItem(seenKey, "true");
    } catch {
      // The in-memory dismissal still works for this visit.
    }
  };

  const showGuide = () => {
    markSeen();
    setOpen(true);
  };

  const toggleStep = (id: string) => {
    setCompleted((current) => {
      const next = current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id];
      saveCompleted(progressKey, next);
      return next;
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    const previousFocus = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
      if (event.key !== "Tab") return;
      const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled])',
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      previousFocus?.focus();
    };
  }, [open]);

  const allDone = completed.length === steps.length;

  return (
    <div className="product-guide">
      {showNudge ? (
        <div className="product-guide__nudge" role="status">
          <Lightbulb aria-hidden="true" size={17} weight="fill" />
          <span>{copy.nudge}</span>
          <button
            aria-label={locale === "zh" ? "关闭首次使用提示" : "Dismiss first-use hint"}
            onClick={markSeen}
            type="button"
          >
            <X aria-hidden="true" size={14} />
          </button>
        </div>
      ) : null}
      <button
        aria-label={locale === "zh" ? "打开使用指引" : "Open product guide"}
        className="product-guide__trigger"
        onClick={showGuide}
        title={locale === "zh" ? "使用指引" : "Product guide"}
        type="button"
      >
        <BookOpenText aria-hidden="true" size={18} />
        <span>{locale === "zh" ? "使用指引" : "Guide"}</span>
      </button>
      {open ? (
        <>
          <button
            aria-hidden="true"
            className="product-guide__scrim"
            onClick={() => setOpen(false)}
            tabIndex={-1}
            type="button"
          />
          <section
            aria-labelledby={`product-guide-title-${experience}`}
            aria-modal="true"
            className="product-guide__panel"
            ref={panelRef}
            role="dialog"
          >
            <header className="product-guide__header">
              <div>
                <span>{copy.eyebrow}</span>
                <h2 id={`product-guide-title-${experience}`}>{copy.title}</h2>
                <p>{copy.intro}</p>
              </div>
              <button
                aria-label={locale === "zh" ? "关闭使用指引" : "Close product guide"}
                className="icon-button"
                onClick={() => setOpen(false)}
                ref={closeRef}
                type="button"
              >
                <X aria-hidden="true" size={20} />
              </button>
            </header>
            <div className={`product-guide__progress product-guide__progress--${experience}`}>
              <span>{locale === "zh" ? `${completed.length} / ${steps.length} 已完成` : `${completed.length} / ${steps.length} complete`}</span>
              <div aria-hidden="true">
                {steps.map((step) => <i className={completed.includes(step.id) ? "is-complete" : undefined} key={step.id} />)}
              </div>
            </div>
            <ol className="product-guide__steps">
              {steps.map((step, index) => {
                const done = completed.includes(step.id);
                const markLabel = locale === "zh"
                  ? `标记“${step.title}”为${done ? "未完成" : "已完成"}`
                  : `Mark “${step.title}” ${done ? "incomplete" : "complete"}`;
                return (
                  <li className={done ? "is-complete" : undefined} key={step.id}>
                    <button aria-label={markLabel} className="product-guide__check" onClick={() => toggleStep(step.id)} type="button">
                      {done ? <Check aria-hidden="true" size={14} weight="bold" /> : <Circle aria-hidden="true" size={14} />}
                    </button>
                    <Link
                      onClick={() => {
                        if (!done) toggleStep(step.id);
                        setOpen(false);
                      }}
                      to={pathWithLocale(step.to, locale)}
                    >
                      <span>{String(index + 1).padStart(2, "0")} · {step.time}</span>
                      <strong>{step.title}</strong>
                      <p>{step.description}</p>
                    </Link>
                    <ArrowRight aria-hidden="true" size={16} />
                  </li>
                );
              })}
            </ol>
            <footer className={allDone ? "is-complete" : undefined}>
              {allDone ? <CheckCircle aria-hidden="true" size={19} weight="fill" /> : <Lightbulb aria-hidden="true" size={19} weight="fill" />}
              <span>{allDone ? (locale === "zh" ? "体验路线已完成，你可以随时回来重新查看。" : "Tour complete. You can reopen it whenever you need a refresher.") : copy.tip}</span>
            </footer>
          </section>
        </>
      ) : null}
    </div>
  );
}
